import {
  AspectRatio,
  Box,
  DarkMode,
  Flex,
  HStack,
  IconButton,
  Image,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { CenteredContent } from "../../../../../components/CenteredContent";
import { Href } from "../../../../../components/Href";
import { Icon } from "../../../../../components/Icon";
import { Z_INDEX } from "../../../../../constants/zIndex";
import { MenuSearch } from "../Search/Menu";
import { SearchResult } from "../Search/Results";
import { MenuMobile } from "./Menu";
import { SocialNetworks } from "./SocialNetworks";

export function HeaderMobile() {
  const intl = useIntl();

  const router = useRouter();

  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const ref = useRef(/** @type {HTMLDivElement | null} */ (null));

  const focusSearchContainer = useCallback(() => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsSearchOpened(false);
      setIsMenuOpened(false);
    }
  }, [isMobile]);

  const handleClickSearch = useCallback(() => {
    setIsMenuOpened(false);
    setIsSearchOpened((prev) => !prev);
  }, []);

  const handleClickMenu = useCallback(() => {
    setIsSearchOpened(false);
    setIsMenuOpened((prev) => !prev);
  }, []);

  const isOpened = isSearchOpened || isMenuOpened;

  useEffect(() => {
    if (isOpened) {
      focusSearchContainer();
    }
  }, [focusSearchContainer, isOpened]);

  useEffect(() => {
    const handleRouteChange = () => {
      setIsSearchOpened(false);
      setIsMenuOpened(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("hashChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("hashChangeStart", handleRouteChange);
    };
  }, [router]);

  return (
    <DarkMode>
      <Box
        display={{ base: "block", lg: "none" }}
        zIndex={Z_INDEX.HEADER_MOBILE}
        backgroundColor="brandPrimary.700"
        px="1rem"
        py="0.75rem">
        <CenteredContent>
          <HStack justifyContent="space-between" spacing="1rem">
            <Href href={generateFrontPath({ id: "@front.home" })}>
              <AspectRatio w="113px" ratio={113 / 48}>
                <Image
                  src={generateFrontPath({
                    id: "@front.internal-assets",
                    parameters: { filePath: "logo.svg" },
                    includeBasePath: true,
                  })}
                  alt="La Bresse - Hautes Vosges"
                />
              </AspectRatio>
            </Href>

            <HStack spacing=".25rem">
              <IconButton
                onClick={handleClickSearch}
                icon={
                  <Icon
                    icon={isSearchOpened ? "ms_close" : "ms_search"}
                    size="24px"
                  />
                }
                variant="ghost"
                aria-label={
                  isSearchOpened
                    ? intl.formatMessage({
                        defaultMessage: "Fermer la recherche",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Rechercher",
                      })
                }
              />

              <IconButton
                onClick={handleClickMenu}
                icon={
                  <Icon
                    icon={isMenuOpened ? "ms_close" : "ms_menu"}
                    size="24px"
                  />
                }
                variant="ghost"
                aria-label={
                  isSearchOpened
                    ? intl.formatMessage({ defaultMessage: "Fermer le menu" })
                    : intl.formatMessage({
                        defaultMessage: "Ouvrir le menu",
                      })
                }
              />
            </HStack>
          </HStack>
        </CenteredContent>
      </Box>

      <Flex
        ref={ref}
        flexDirection="column"
        role="dialog"
        aria-modal="true"
        aria-hidden={isOpened ? "false" : "true"}
        tabIndex={-1}
        visibility={isOpened ? "visible" : "hidden"}
        opacity={isOpened ? 1 : 0}
        transition="all .25s ease"
        position="fixed"
        top="72px"
        left="0"
        right="0"
        bottom="0"
        backgroundColor="brandPrimary.700"
        overflowY="hidden">
        <Box px="1rem">
          <Box borderTopWidth="1px" borderColor="gray.400" />
        </Box>

        {isSearchOpened && (
          <Box overflowY="auto" p="1rem">
            <VStack spacing="1.25rem">
              <SearchResult />

              <MenuSearch />
            </VStack>
          </Box>
        )}

        {isMenuOpened && (
          <Flex flexGrow={1} flexDirection="column" overflowY="auto" p="1rem">
            <Box>
              <MenuMobile
                onClickSearch={handleClickSearch}
                onClickMenu={handleClickMenu}
              />
            </Box>

            <Box flexShrink={0}>
              <SocialNetworks />
            </Box>
          </Flex>
        )}
      </Flex>
    </DarkMode>
  );
}
